import { useRef, useEffect, useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { BlinkoGameEngine } from '../game-engine/game-engine';
import { BlinkoRenderer } from '../game-engine/renderer';
import { GameLoop } from '../components/GameLoop';
import { computeLevelProgress, computeReachedLevel } from '../game-engine';
import { BlinkoMode, BlinkoVersion, type LevelConfig } from '../game-engine/constants';
import type { SoundKey } from '../components/SoundHandler';
import {
  atomCharacterChargeState,
  atomCharacterChargeProgress,
  atomCharacterDropQueue,
  CharacterChargeState,
} from '../states/game.atoms';

interface BinHitEvent {
  payload: {
    bin: number;
  };
}

interface SpecialPinHitEvent {
  payload?: {
    experience?: number;
  };
}

interface PlaySoundEvent {
  payload?: {
    soundType: string;
  };
}

interface ModeChangeEvent {
  payload?: {
    mode: BlinkoMode;
  };
}

interface UseGameEngineProps {
  scale: number;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onBallHitBin: (event: BinHitEvent) => void;
  onSpecialPinHit: (event: SpecialPinHitEvent) => void;
  onPlaySound: (event: PlaySoundEvent) => void;
  onModeChange?: (mode: BlinkoMode) => void;
  onLetterActivated?: (event?: unknown) => void;
  level?: number; // current level for multiplier and UI
  experience?: number; // total experience (for computing level progress)

  ethUsdPrice?: number;
  soundPlayer?: (sound: SoundKey) => void;
  levelConfig: LevelConfig;
  isHardMode: boolean;
  version: BlinkoVersion;
}

export const useGameEngine = ({
  scale,
  canvasRef,
  onBallHitBin,
  onSpecialPinHit,
  onPlaySound,
  onModeChange,
  onLetterActivated,
  level = 1,
  experience = 0,
  soundPlayer,
  levelConfig,
  isHardMode,
  version,
  physicsSteps = 1,
}: UseGameEngineProps & { physicsSteps?: number }) => {

  const engineRef = useRef<BlinkoGameEngine | null>(null);
  const rendererRef = useRef<BlinkoRenderer | null>(null);

  // Character animation atoms
  const setCharacterChargeState = useSetAtom(atomCharacterChargeState);
  const setCharacterChargeProgress = useSetAtom(atomCharacterChargeProgress);
  const setCharacterDropQueue = useSetAtom(atomCharacterDropQueue);
  const gameLoopRef = useRef<GameLoop | null>(null);
  const hitBinsRef = useRef<number[]>([]);
  const binTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const levelRef = useRef<number>(level);
  const modeChangeRef = useRef<UseGameEngineProps['onModeChange']>(onModeChange);
  const letterActivatedRef = useRef<UseGameEngineProps['onLetterActivated']>(onLetterActivated);
  const ballHitBinRef = useRef<UseGameEngineProps['onBallHitBin']>(onBallHitBin);
  const specialPinHitRef = useRef<UseGameEngineProps['onSpecialPinHit']>(onSpecialPinHit);
  const playSoundRef = useRef<UseGameEngineProps['onPlaySound']>(onPlaySound);

  const modeRef = useRef<BlinkoMode>('main');
  const hardModeRef = useRef<boolean>(!!isHardMode);
  const levelConfigRef = useRef<LevelConfig>(levelConfig);
  const versionRef = useRef<BlinkoVersion>(version);

  useEffect(() => {
    modeChangeRef.current = onModeChange;
  }, [onModeChange]);

  useEffect(() => {
    letterActivatedRef.current = onLetterActivated;
  }, [onLetterActivated]);

  useEffect(() => {
    ballHitBinRef.current = onBallHitBin;
  }, [onBallHitBin]);

  useEffect(() => {
    specialPinHitRef.current = onSpecialPinHit;
  }, [onSpecialPinHit]);

  useEffect(() => {
    playSoundRef.current = onPlaySound;
  }, [onPlaySound]);

  useEffect(() => {
    hardModeRef.current = !!isHardMode;
  }, [isHardMode]);

  useEffect(() => {
    versionRef.current = version
    if (engineRef.current) {
      engineRef.current.setVersion(version);
    }
  }, [version]);

  useEffect(() => {
    levelConfigRef.current = levelConfig;
  }, [levelConfig]);

  useEffect(() => {
    if (!engineRef.current) {
      engineRef.current = new BlinkoGameEngine();
      engineRef.current.setVersion(versionRef.current);

      // Init renderer
      rendererRef.current = new BlinkoRenderer();
      rendererRef.current.setScale(scale);

      // Set up event listeners
      engineRef.current.on('ball-hit-bin', (event: BinHitEvent) => {
        if (!event.payload) {
          return;
        }

        // Animate bin hit
        const { bin } = event.payload;
        hitBinsRef.current.push(bin);

        // Clear existing timeout if any
        if (binTimeoutsRef.current.has(bin)) {
          clearTimeout(binTimeoutsRef.current.get(bin)!);
        }

        // Set timeout to remove
        const timeout = setTimeout(() => {
          hitBinsRef.current = hitBinsRef.current.filter(b => b !== bin);
          binTimeoutsRef.current.delete(bin);
        }, 100);

        binTimeoutsRef.current.set(bin, timeout);

        // Call original handler via Ref
        ballHitBinRef.current?.(event as unknown as BinHitEvent);

        rendererRef.current?.notifyBinScored?.(bin);
      });
      engineRef.current.on('special-pin-hit', (event) => specialPinHitRef.current?.(event as unknown as SpecialPinHitEvent));
      engineRef.current.on('play-sound', (event) => playSoundRef.current?.(event as unknown as PlaySoundEvent));
      engineRef.current.on('mode-changed', (event) => {
        const payload = (event as unknown as ModeChangeEvent)?.payload;
        const mode: BlinkoMode = payload?.mode ?? 'main';
        modeRef.current = mode;
        modeChangeRef.current?.(mode);
      });
      engineRef.current.on('letter-activated', (event) => {
        letterActivatedRef.current?.(event);
      });
    }

    return () => {
      // Clear all bin timeouts
      binTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      binTimeoutsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (rendererRef.current) {
      const config = levelConfigRef.current;
      const achieved = computeReachedLevel(experience, config);
      const progress = computeLevelProgress(level, experience, config);
      rendererRef.current?.setLevelUI?.(level, progress, achieved);
    }
  }, [level, experience, levelConfig]);

  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.setScale(scale);
    }
  }, [scale]);

  // Add ball via character animation
  const addBall = useCallback((seed: number, letterSeed?: number) => {
    const dropBall = () => {
      if (letterSeed !== undefined) {
        engineRef.current?.initializeLetters(letterSeed);
      }
      engineRef.current?.addBall(seed);
    };

    // Queue the drop via atoms for BoardLayer to handle
    setCharacterDropQueue(prev => [...prev, dropBall]);
  }, [setCharacterDropQueue]);

  const initializeLetters = (seed: number, explicitBins?: number[]) => {
    engineRef.current?.initializeLetters(seed, explicitBins);
  };

  // Game loop using GameLoop utility
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !engineRef.current) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    // Clean previous loop if any
    if (gameLoopRef.current) {
      gameLoopRef.current.clear();
      gameLoopRef.current = null;
    }

    const update = () => {
      for (let i = 0; i < physicsSteps; i++) {
        engineRef.current!.update();
        rendererRef.current?.update(1 / 60);
      }
    };

    const render = () => {
      const renderingParams = {
        hitBins: new Set(hitBinsRef.current),
        pinFx: engineRef.current!.getPinFx(),
        letters: engineRef.current!.getLetters(),
        mode: engineRef.current!.getMode(),
        hardMode: hardModeRef.current,
      };
      rendererRef.current?.render(
        ctx,
        engineRef.current!.getBalls(),
        renderingParams,
      );
    };

    const gl = new GameLoop({ fps: 60, update, render });
    gameLoopRef.current = gl;
    gl.start();

    return () => {
      gl.stop();
      if (gameLoopRef.current === gl) {
        gameLoopRef.current = null;
      }
    };
  }, [canvasRef, scale]);



  const setCharacterState = useCallback((state: CharacterChargeState, progress: number) => {
    setCharacterChargeState(state);
    setCharacterChargeProgress(progress);
  }, [setCharacterChargeState, setCharacterChargeProgress]);

  return {
    addBall,
    getBallCount: () => engineRef.current?.getBallCount() || 0,
    initializeLetters,
    setLettersActive: (active: boolean[]) => engineRef.current?.setLettersActive(active),
    setExternalLetterControl: (on: boolean) => engineRef.current?.setExternalLetterControl(on),
    setCharacterState,
  };
};
